import api from './authService';
import { ApiResponse } from '../types';

export interface S3Config {
    use_s3: boolean;
    bucket_name: string;
    region: string;
    access_key_id: string;
    secret_access_key: string;
    endpoint_url: string;
}

export interface SMTPConfig {
    smtp_host: string;
    smtp_port: number;
    smtp_user: string;
    smtp_password: string;
    smtp_use_tls: boolean;
    smtp_from_address: string;
    smtp_transmittal_use_same: boolean;
    smtp_transmittal_host: string;
    smtp_transmittal_port: number;
    smtp_transmittal_user: string;
    smtp_transmittal_password: string;
    smtp_transmittal_use_tls: boolean;
    smtp_transmittal_from_address: string;
}

export interface NDSignConfig {
    base_url: string;
    api_key: string;
    webhook_secret: string;
    oidc_client_secret: string;
    configured: boolean;
}

export interface DocumentTypeConfig {
    id: number;
    name: string;
    description?: string;
}

export interface DisciplineConfig {
    id: number;
    discipline: string;
    description?: string;
}

export const configurationsService = {
    // Get S3 configuration
    async getS3Config(): Promise<S3Config> {
        const response = await api.get('/configurations/s3');
        return response.data;
    },

    // Update S3 configuration
    async updateS3Config(config: S3Config): Promise<ApiResponse<{ message: string }>> {
        const response = await api.post('/configurations/s3', config);
        return response;
    },

    // Test S3 connection
    async testS3Connection(config: S3Config): Promise<{ message: string; success: boolean }> {
        const response = await api.post('/configurations/s3/test', config);
        return response.data;
    },

    // Get SMTP configuration
    async getSMTPConfig(): Promise<SMTPConfig> {
        const response = await api.get('/configurations/smtp');
        return response.data;
    },

    // Update SMTP configuration
    async updateSMTPConfig(config: SMTPConfig): Promise<ApiResponse<{ message: string }>> {
        const response = await api.post('/configurations/smtp', config);
        return response;
    },

    // Test SMTP connection
    async testSMTPConnection(
        config: SMTPConfig & { test_email: string; account?: 'general' | 'transmittal' }
    ): Promise<{ message: string; success: boolean }> {
        const response = await api.post('/configurations/smtp/test', config);
        return response.data;
    },

    async getNDSignConfig(): Promise<NDSignConfig> {
        const response = await api.get('/configurations/ndsign');
        return response.data;
    },

    async updateNDSignConfig(config: Omit<NDSignConfig, 'configured'>): Promise<ApiResponse<{ message: string }>> {
        const response = await api.post('/configurations/ndsign', config);
        return response;
    },

    async testNDSignConnection(config: Pick<NDSignConfig, 'base_url' | 'api_key'>): Promise<{ message: string; success: boolean }> {
        const response = await api.post('/configurations/ndsign/test', config);
        return response.data;
    },

    // Revisions
    async getRevisions(): Promise<any[]> {
        const response = await api.get('/configurations/revisions');
        return response.data;
    },

    async addRevision(revision: any): Promise<{ message: string; id: number }> {
        const response = await api.post('/configurations/revisions', revision);
        return response.data;
    },

    async updateRevision(id: number, revision: any): Promise<{ message: string }> {
        const response = await api.put(`/configurations/revisions/${id}`, revision);
        return response.data;
    },

    async deleteRevision(id: number): Promise<{ message: string }> {
        const response = await api.delete(`/configurations/revisions/${id}`);
        return response.data;
    },

    // Document types
    async getDocumentTypes(): Promise<DocumentTypeConfig[]> {
        const response = await api.get('/configurations/document-types');
        return response.data;
    },

    async addDocumentType(payload: Partial<DocumentTypeConfig>): Promise<{ message: string; id: number }> {
        const response = await api.post('/configurations/document-types', payload);
        return response.data;
    },

    async updateDocumentType(id: number, payload: Partial<DocumentTypeConfig>): Promise<{ message: string }> {
        const response = await api.put(`/configurations/document-types/${id}`, payload);
        return response.data;
    },

    async deleteDocumentType(id: number): Promise<{ message: string }> {
        const response = await api.delete(`/configurations/document-types/${id}`);
        return response.data;
    },

    // Disciplines
    async getDisciplines(): Promise<DisciplineConfig[]> {
        const response = await api.get('/configurations/disciplines');
        return response.data;
    },

    async addDiscipline(payload: Partial<DisciplineConfig>): Promise<{ message: string; id: number }> {
        const response = await api.post('/configurations/disciplines', payload);
        return response.data;
    },

    async updateDiscipline(id: number, payload: Partial<DisciplineConfig>): Promise<{ message: string }> {
        const response = await api.put(`/configurations/disciplines/${id}`, payload);
        return response.data;
    },

    async deleteDiscipline(id: number): Promise<{ message: string }> {
        const response = await api.delete(`/configurations/disciplines/${id}`);
        return response.data;
    },

    // Gallery
    async getGalleryImages(): Promise<{ images: string[] }> {
        const response = await api.get('/configurations/gallery');
        return response.data;
    }
};
